import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { subscriptionsAPI } from '../../services/api';
import { CreditCard, X, AlertCircle, Calendar, CheckCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

interface Subscription {
  id: number;
  child_name: string;
  program_name: string;
  level_name: string;
  status: string;
  monthly_amount: string;
  card_brand: string;
  last_four: string;
  next_billing_date: string;
  sessions_completed: number;
  sessions_total: number;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
}

const MySubscriptions: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const { data: subscriptions, isLoading } = useQuery<Subscription[]>(
    'mySubscriptions',
    async () => {
      const res = await subscriptionsAPI.getMySubscriptions();
      return res.data;
    }
  );

  const cancelMutation = useMutation(
    ({ id, reason }: { id: number; reason: string }) => 
      subscriptionsAPI.cancelMySubscription(id, reason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('mySubscriptions');
        setShowCancelModal(false);
        setSelectedSub(null);
        setCancelReason('');
      }
    }
  );

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      trialing: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status === 'active' && <CheckCircle size={14} className="inline mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  const handleCancelClick = (sub: Subscription) => {
    setSelectedSub(sub);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (selectedSub) {
      cancelMutation.mutate({ id: selectedSub.id, reason: cancelReason });
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Subscriptions</h1>
        <p className="text-gray-600 mt-1">Manage your monthly program subscriptions</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-primary-500" size={32} />
        </div>
      ) : subscriptions?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <CreditCard className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Subscriptions</h3>
          <p className="text-gray-600">
            You don't have any active subscriptions yet. Register for a program to get started!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {subscriptions?.map((sub) => (
            <motion.div
              key={sub.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{sub.program_name}</h3>
                    {getStatusBadge(sub.status)}
                  </div>
                  
                  <div className="text-gray-600 space-y-1">
                    <p><strong>Child:</strong> {sub.child_name}</p>
                    {sub.level_name && <p><strong>Level:</strong> {sub.level_name}</p>}
                    <p><strong>Monthly Amount:</strong> ${sub.monthly_amount}</p>
                    {sub.card_brand && sub.last_four && (
                      <p className="flex items-center gap-2">
                        <CreditCard size={16} />
                        <strong>Payment Method:</strong> {sub.card_brand} •••• {sub.last_four}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 flex items-center space-x-6 text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span>
                        Next billing: {sub.next_billing_date 
                          ? new Date(sub.next_billing_date).toLocaleDateString() 
                          : 'N/A'}
                      </span>
                    </div>
                    <div>
                      Sessions: {sub.sessions_completed}/{sub.sessions_total} completed
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary-500 h-2 rounded-full transition-all" 
                        style={{ width: `${(sub.sessions_completed / sub.sessions_total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 md:mt-0 md:ml-6">
                  {sub.status === 'active' && (
                    <button
                      onClick={() => handleCancelClick(sub)}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Cancel Subscription
                    </button>
                  )}
                  {sub.status === 'canceled' && (
                    <span className="text-gray-500 text-sm">Canceled</span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedSub && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Cancel Subscription</h2>
              <button onClick={() => setShowCancelModal(false)}>
                <X size={24} className="text-gray-500" />
              </button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-yellow-800">Are you sure?</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Canceling will stop future billing for <strong>{selectedSub.program_name}</strong> 
                    ({selectedSub.child_name}). This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Please let us know why you're canceling..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={cancelMutation.isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isLoading ? (
                  <Loader2 className="animate-spin mx-auto" size={20} />
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MySubscriptions;
